import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'logic/moment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Avatar from '@material-ui/core/Avatar';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import CalendarIcon from '@material-ui/icons/EventBusy';
import BookingIcon from '@material-ui/icons/PlaylistAddCheck';
import NewsIcon from '@material-ui/icons/Announcement';
import Alert from '@material-ui/lab/Alert';
import { FormattedMessage } from 'react-intl';
import CircularProgress from 'components/shared/CircularProgress';

const styles = makeStyles((theme) => ({
  li: {
    backgroundColor: 'white',
  },
  item: {
    '& a': {
      color: 'inherit',
      textDecoration: 'none',

      '&:hover': {
        color: 'inherit',
        textDecoration: 'none',
      },
    },
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
  },
  avatarAlt: {
    backgroundColor: theme.palette.secondary.light,
  },
  avatarDisabled: {
    backgroundColor: '#b9b9b9',
  },
}));

const PendingBooking = ({ todo }) => {
  const classes = styles();

  return (
    <ListItem
      divider
      button
      classes={{ root: classes.li }}
      component={Link}
      to={`/conversations/${todo.value.conversation_id}/bookings/${todo.value.id}`}
    >
      <ListItemAvatar>
        <Avatar className={classes.avatarAlt}>
          <BookingIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        classes={{
          primary: classes.item,
        }}
        primary={(
          <FormattedMessage
            id="todo.pendingBooking"
            defaultMessage="You have received a booking offer to consider for ad {cottage}, from {checkin} to {checkout}"
            values={{
              cottage: `#${todo.value.Cottage.id} - "${todo.value.Cottage.title_sv}"`,
              checkin: moment(todo.value.check_in).format('LLL'),
              checkout: moment(todo.value.check_out).format('LLL'),
            }}
          />
        )}
      />
    </ListItem>
  );
};

PendingBooking.propTypes = {
  todo: PropTypes.shape({
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})]),
  }),
};

const UnreadMessages = ({ todo }) => {
  const classes = styles();

  return (
    <ListItem
      divider
      button
      classes={{ root: classes.li }}
      component={Link}
      to="/conversations"
    >
      <ListItemAvatar>
        <Avatar className={classes.avatar}>
          <NewsIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        classes={{
          primary: classes.item,
        }}
        primary={(
          <FormattedMessage
            id="todo.unreadMessages"
            defaultMessage="You have {count} unread messages"
            values={{
              count: todo.value,
            }}
          />
        )}
      />
    </ListItem>
  );
};

UnreadMessages.propTypes = {
  todo: PropTypes.shape({
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})]),
  }),
};

const OutdatedCottages = ({ todo }) => {
  const classes = styles();

  return (
    <ListItem
      button
      divider
      classes={{ root: classes.li }}
      component={Link}
      to={`/cottages/${todo.value.id}/calendar`}
    >
      <ListItemAvatar>
        <Avatar className={classes.avatar}>
          <CalendarIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        classes={{
          primary: classes.item,
        }}
        primary={(
          <FormattedMessage
            id="todo.outdatedCottages"
            defaultMessage="Your calendar for ad #{id} - {title} has not been updated for the last 3 months. Click here to update it"
            values={{
              id: todo.value.id,
              title: `"${todo.value.title}"`,
            }}
          />
        )}
      />
    </ListItem>
  );
};

OutdatedCottages.propTypes = {
  todo: PropTypes.shape({
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})]),
  }),
};

const UnreadNews = ({ todo }) => {
  const classes = styles();

  return (
    <ListItem
      button
      divider
      classes={{ root: classes.li }}
      component={Link}
      to={`/news/${todo.value.id}`}
    >
      <ListItemAvatar>
        <Avatar className={todo.value.read ? classes.avatarDisabled : classes.avatar}>
          <NewsIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        classes={{
          primary: classes.item,
        }}
        primary={(
          <FormattedMessage
            id="todo.unreadNews"
            defaultMessage="Read the news about: {title}"
            values={{
              title: todo.value.title,
            }}
          />
        )}
      />
    </ListItem>
  );
};

UnreadNews.propTypes = {
  todo: PropTypes.shape({
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})]),
  }),
};

const Todo = (todo, i) => {
  switch (todo.type) {
    case 'UNREAD_MESSAGES': {
      return (<UnreadMessages todo={todo} key={i} />);
    }
    case 'OUTDATED_COTTAGE': {
      return (<OutdatedCottages todo={todo} key={i} />);
    }
    case 'PENDING_BOOKING': {
      return (<PendingBooking todo={todo} key={i} />);
    }
    case 'UNREAD_NEWS': {
      return (<UnreadNews todo={todo} key={i} />);
    }
    default: {
      return null;
    }
  }
};

const Todos = ({ todos }) => {
  if (!todos) {
    return (
      <div>
        <CircularProgress />
      </div>
    );
  }

  if (!todos.length) {
    return (
      <>
        <Alert severity="success" elevation={3}>
          <FormattedMessage
            id="todo.empty"
            defaultMessage="You don't have any tasks in your todo list"
          />
        </Alert>
      </>
    );
  }

  return (
    <>
      <List
        component="nav"
      >
        {todos.map(Todo)}
      </List>
    </>
  );
};

Todos.propTypes = {
  todos: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})]),
    }),
  ),
};

export default Todos;
